import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const STRIPE_STARTER = 'https://buy.stripe.com/test_eVq28sgv1axh0L0eQl73G02';
const STRIPE_PRO     = 'https://buy.stripe.com/test_cNibJ22Eb8p9alAcId73G03';
const WA_LINK        = 'https://wa.me/491791548222';

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: '49',
    period: '/ Monat',
    desc: 'Perfekt für kleine Betriebe die starten wollen.',
    features: [
      '1 KI-Agent',
      'Bis zu 200 Anrufe / Monat',
      'Terminbuchung & Weiterleitungen',
      'E-Mail-Support',
    ],
    highlight: false,
    cta: 'Jetzt starten',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '99',
    period: '/ Monat',
    desc: 'Für wachsende Unternehmen mit mehr Volumen.',
    features: [
      '3 KI-Agenten',
      'Bis zu 1.000 Anrufe / Monat',
      'Terminbuchung, CRM & Analytics',
      'Prioritäts-Support',
      'Eigene Stimme & Skript',
    ],
    highlight: true,
    cta: 'Pro wählen',
  },
  {
    id: 'enterprise',
    name: 'Individuell',
    price: 'Auf Anfrage',
    period: '',
    desc: 'Maßgeschneiderte Lösung für dein Unternehmen.',
    features: [
      'Unbegrenzte Agenten',
      'Unbegrenzte Anrufe',
      'Individuelle Integrationen',
      'Dedicated Support & SLA',
      'On-Premise Option',
    ],
    highlight: false,
    cta: 'Kontakt aufnehmen',
  },
];

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

function PlanSelectPage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [selected, setSelected] = useState('pro');
  const [loading, setLoading] = useState(false);

  const handleContinue = () => {
    setLoading(true);
    if (selected === 'starter') {
      window.location.href = STRIPE_STARTER;
    } else if (selected === 'pro') {
      window.location.href = STRIPE_PRO;
    } else {
      // Individuell → WhatsApp Kontakt
      window.open(WA_LINK, '_blank');
      setLoading(false);
    }
  };

  const getCtaLabel = () => {
    if (selected === 'enterprise') return 'Kontakt aufnehmen';
    return 'Jetzt kaufen & loslegen →';
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.logo} onClick={() => navigate('/')}>
          <img src="/logo-frontup.png" style={{width:32,height:32,borderRadius:8,objectFit:'cover'}} alt="FrontUp" />
          <span style={styles.logoName}>FrontUp</span>
        </div>
        <div style={{fontSize:13,color:'#888'}}>
          {user?.email} &nbsp;·&nbsp;
          <button onClick={() => { signOut(); navigate('/'); }} style={styles.logoutBtn}>Abmelden</button>
        </div>
      </div>

      <div style={styles.body}>
        <div style={{fontSize:12,fontWeight:700,letterSpacing:'2px',textTransform:'uppercase',color:'#888',marginBottom:12,textAlign:'center'}}>Schritt 1 von 1</div>
        <h1 style={styles.title}>Wähle deinen Plan</h1>
        <p style={styles.sub}>Kein versteckter Preis. Jederzeit kündbar.</p>

        <div style={styles.grid}>
          {PLANS.map(p => (
            <div
              key={p.id}
              onClick={() => setSelected(p.id)}
              style={{
                ...styles.card,
                ...(p.highlight ? styles.cardHighlight : {}),
                ...(selected === p.id ? (p.highlight ? styles.cardSelectedHighlight : styles.cardSelected) : {}),
              }}
            >
              {p.highlight && (
                <div style={styles.badge}>Beliebteste Wahl</div>
              )}

              <div style={{fontSize:13,fontWeight:700,letterSpacing:'1px',textTransform:'uppercase',color: p.highlight ? 'rgba(255,255,255,.5)' : '#aaa',marginBottom:10}}>{p.name}</div>

              <div style={{display:'flex',alignItems:'baseline',gap:4,marginBottom:8}}>
                <span style={{fontSize: p.price === 'Auf Anfrage' ? 22 : 36,fontWeight:800,letterSpacing:'-1.5px',color: p.highlight ? '#fff' : '#111'}}>
                  {p.price === 'Auf Anfrage' ? p.price : `€${p.price}`}
                </span>
                {p.period && <span style={{fontSize:14,color: p.highlight ? 'rgba(255,255,255,.4)' : '#aaa'}}>{p.period}</span>}
              </div>

              <p style={{fontSize:14,color: p.highlight ? 'rgba(255,255,255,.5)' : '#888',marginBottom:20,lineHeight:1.5}}>{p.desc}</p>

              <ul style={styles.features}>
                {p.features.map((f, i) => (
                  <li key={i} style={{...styles.feature, color: p.highlight ? 'rgba(255,255,255,.8)' : '#333'}}>
                    <span style={{color: p.highlight ? 'rgba(255,255,255,.6)' : '#111', flexShrink:0}}><CheckIcon /></span>
                    {f}
                  </li>
                ))}
              </ul>

              <div style={{
                marginTop: 'auto',
                paddingTop: 20,
                width: 20,
                height: 20,
                borderRadius: '50%',
                border: `2px solid ${selected === p.id ? (p.highlight ? '#fff' : '#111') : (p.highlight ? 'rgba(255,255,255,.3)' : '#ccc')}`,
                background: selected === p.id ? (p.highlight ? '#fff' : '#111') : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                alignSelf: 'flex-start',
              }}>
                {selected === p.id && (
                  <div style={{width:8,height:8,borderRadius:'50%',background: p.highlight ? '#111' : '#fff'}} />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* INFO BOX für Individuell */}
        {selected === 'enterprise' && (
          <div style={styles.infoBox}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#888" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>Du wirst direkt per WhatsApp mit uns verbunden. Wir melden uns innerhalb von 24 Stunden.</span>
          </div>
        )}

        <button onClick={handleContinue} disabled={loading} style={{
          ...styles.continueBtn,
          ...(selected === 'enterprise' ? styles.continueBtnContact : {}),
        }}>
          {loading ? 'Weiter...' : getCtaLabel()}
        </button>

        {selected !== 'enterprise' && (
          <p style={{textAlign:'center',fontSize:13,color:'#aaa',marginTop:12}}>
            Sichere Zahlung über Stripe · Jederzeit kündbar · Keine versteckten Kosten
          </p>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f7f7f7',
  },
  header: {
    background: '#fff',
    borderBottom: '1px solid #e5e5e5',
    padding: '0 40px',
    height: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
  },
  logoName: {
    fontSize: 18,
    fontWeight: 800,
    letterSpacing: '-.5px',
    color: '#111',
  },
  logoutBtn: {
    background: 'none',
    border: 'none',
    fontSize: 13,
    color: '#888',
    cursor: 'pointer',
    fontFamily: 'inherit',
    padding: 0,
  },
  body: {
    maxWidth: 1000,
    margin: '0 auto',
    padding: '64px 40px',
  },
  title: {
    fontSize: 'clamp(32px,4vw,48px)',
    fontWeight: 800,
    letterSpacing: '-1.5px',
    color: '#111',
    textAlign: 'center',
    marginBottom: 10,
  },
  sub: {
    fontSize: 16,
    color: '#888',
    textAlign: 'center',
    marginBottom: 48,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3,1fr)',
    gap: 16,
    marginBottom: 24,
  },
  card: {
    background: '#fff',
    border: '2px solid #e5e5e5',
    borderRadius: 16,
    padding: '28px 24px',
    cursor: 'pointer',
    transition: 'border-color .15s',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  cardHighlight: {
    background: '#111',
    border: '2px solid #111',
  },
  cardSelected: {
    border: '2px solid #111',
  },
  cardSelectedHighlight: {
    border: '2px solid #444',
  },
  badge: {
    position: 'absolute',
    top: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#fff',
    border: '1.5px solid #e5e5e5',
    borderRadius: 100,
    padding: '4px 14px',
    fontSize: 12,
    fontWeight: 700,
    color: '#111',
    whiteSpace: 'nowrap',
  },
  features: {
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  feature: {
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  infoBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#fff',
    border: '1.5px solid #e5e5e5',
    borderRadius: 10,
    padding: '12px 16px',
    fontSize: 13,
    color: '#666',
    marginBottom: 16,
    maxWidth: 600,
    margin: '0 auto 16px',
  },
  continueBtn: {
    display: 'block',
    width: '100%',
    maxWidth: 400,
    margin: '0 auto',
    padding: '16px',
    background: '#111',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    transition: 'background .15s',
  },
  continueBtnContact: {
    background: '#25D366',
  },
};

export default PlanSelectPage;
